// AIDEV-NOTE: base-service; Base class for third-party service integrations

export interface ServiceConfig {
  apiBase: string
  headers?: Record<string, string>
  timeout?: number
  retryConfig?: {
    maxRetries: number
    retryDelay: number
  }
}

export class ServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message)
    this.name = 'ServiceError'
  }
}

export abstract class BaseThirdPartyService {
  protected config: ServiceConfig

  constructor(config: ServiceConfig) {
    this.config = config
  }

  protected async fetchApi<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const controller = new AbortController()
    const timeoutId = this.config.timeout
      ? setTimeout(() => controller.abort(), this.config.timeout)
      : null

    try {
      const response = await fetch(`${this.config.apiBase}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...this.config.headers,
          ...options?.headers,
        },
        signal: controller.signal,
      })

      if (timeoutId) clearTimeout(timeoutId)

      if (!response.ok) {
        await this.handleErrorResponse(response)
      }

      return await response.json()
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId)

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ServiceError('Request timeout', 408)
        }
        throw error
      }

      throw new ServiceError('Unknown error occurred')
    }
  }

  protected async handleErrorResponse(response: Response): Promise<never> {
    let errorData
    try {
      errorData = await response.json()
    } catch {
      errorData = { message: 'Unknown error' }
    }

    const message = this.getErrorMessage(response.status, errorData)
    throw new ServiceError(message, response.status, errorData)
  }

  protected getErrorMessage(status: number, errorData: unknown): string {
    // Override in subclasses for service-specific error messages
    switch (status) {
      case 401:
        return 'Authentication failed. Please check your credentials.'
      case 403:
        return 'Access denied. Please check your permissions.'
      case 404:
        return 'Resource not found.'
      case 429:
        return 'Too many requests. Please try again later.'
      case 500:
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.'
      default:
        return (
          (errorData as { message?: string })?.message ||
          `Request failed with status ${status}`
        )
    }
  }
}
